import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Printer, Package, ChevronRight, ArrowLeft, Upload, CheckCircle, AlertCircle,
  RefreshCw, FileText, Clock, Truck, Settings
} from 'lucide-react';

const STATUS_MAP = {
  placed: { label: 'Inviato', color: '#3B82F6' },
  data_check: { label: 'Controllo dati', color: '#F59E0B' },
  in_production: { label: 'In produzione', color: '#8B5CF6' },
  shipped: { label: 'Spedito', color: '#10B981' },
  delivered: { label: 'Consegnato', color: '#64748B' },
  cancelled: { label: 'Annullato', color: '#EF4444' },
};

export default function AdminPrintShop() {
  const [connected, setConnected] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [tab, setTab] = useState('catalog');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Check connection on mount
  useEffect(() => {
    adminApi.get('/admin/flyeralarm/status')
      .then(r => { setConnected(r.data.connected); setStatusMsg(r.data.message); })
      .catch(() => { setConnected(false); setStatusMsg('Errore verifica connessione'); })
      .finally(() => setLoading(false));
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const r = await adminApi.get('/admin/flyeralarm/product-groups');
      setGroups(r.data.groups || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento catalogo'); }
    setCatalogLoading(false);
  }, []);

  const loadGroupDetail = async (groupId) => {
    setCatalogLoading(true);
    try {
      const r = await adminApi.get(`/admin/flyeralarm/product-groups/${groupId}`);
      setGroupDetail(r.data);
    } catch (e) { toast.error('Errore caricamento dettagli'); }
    setCatalogLoading(false);
  };

  const loadOrders = useCallback(async () => {
    try {
      const r = await adminApi.get('/admin/flyeralarm/orders');
      setOrders(r.data.orders || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (connected) {
      loadCatalog();
      loadOrders();
    }
  }, [connected, loadCatalog, loadOrders]);

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="w-8 h-8 border-3 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Not connected - setup guide
  if (!connected) return (
    <div className="p-6 md:p-8 lg:p-10 max-w-2xl" data-testid="flyeralarm-setup">
      <h1 className="text-3xl font-bold tracking-tight text-[#1A1A1A] mb-2">Stampa Marketing</h1>
      <p className="text-[#64748B] text-sm mb-8">Stampa volantini, poster e biglietti da visita per il tuo locale</p>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
            <Settings size={24} className="text-amber-500" />
          </div>
          <div>
            <h2 className="font-bold text-[#1A1A1A]">Configura il servizio di stampa</h2>
            <p className="text-xs text-[#64748B]">{statusMsg}</p>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-[#1A1A1A]">Come abilitare il servizio:</h3>
          <ol className="space-y-2 text-xs text-[#64748B]">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[#D44E20] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
              <span>Contatta il tuo referente per ricevere le credenziali API del servizio di stampa</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[#D44E20] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
              <span>Riceverai username e password via email</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[#D44E20] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
              <span>Inseriscile nelle impostazioni del servizio stampa</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[#D44E20] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
              <span>Avrai accesso al catalogo con migliaia di prodotti stampabili</span>
            </li>
          </ol>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
          <p className="text-[11px] text-blue-700">
            Usa l'<strong>Image Builder AI</strong> per creare il design grafico, poi ordina la stampa direttamente da qui.
            Spedizione diretta al locale con imballo neutro.
          </p>
        </div>
      </div>
    </div>
  );

  // Connected - full interface
  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="flyeralarm-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1A1A1A]">Stampa Marketing</h1>
          <p className="text-[#64748B] text-sm mt-0.5 flex items-center gap-1.5">
            <CheckCircle size={12} className="text-green-500" /> Servizio attivo
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'catalog', label: 'Catalogo prodotti', icon: Package },
          { key: 'orders', label: 'Ordini di stampa', icon: FileText, count: orders.length },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`fa-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === t.key ? 'bg-[#D44E20] text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-700'
            }`}>
            <t.icon size={15} /> {t.label}
            {t.count > 0 && <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Catalog */}
      {tab === 'catalog' && (
        <div className="space-y-3">
          {!selectedGroup ? (
            <>
              <p className="text-xs text-slate-500 mb-2">Seleziona una categoria di prodotti da stampare</p>
              {catalogLoading ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>
              ) : groups.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
                  <Printer size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">Nessun prodotto disponibile</p>
                  <button onClick={loadCatalog} className="mt-3 text-xs text-[#D44E20] font-semibold hover:underline">Ricarica catalogo</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {groups.map(g => (
                    <button key={g.id} onClick={() => { setSelectedGroup(g); loadGroupDetail(g.id); }}
                      data-testid={`group-${g.id}`}
                      className="bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-[#D44E20]/30 hover:shadow-sm transition-all flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#D44E20]/10 flex items-center justify-center">
                          <Printer size={18} className="text-[#D44E20]" />
                        </div>
                        <span className="text-sm font-semibold text-[#1A1A1A]">{g.name}</span>
                      </div>
                      <ChevronRight size={16} className="text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <button onClick={() => { setSelectedGroup(null); setGroupDetail(null); }}
                className="flex items-center gap-1.5 text-sm text-[#D44E20] font-semibold hover:underline mb-3">
                <ArrowLeft size={14} /> Torna al catalogo
              </button>
              <h2 className="text-lg font-bold text-[#1A1A1A] mb-3">{selectedGroup.name}</h2>
              {catalogLoading ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>
              ) : groupDetail ? (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <pre className="text-xs text-slate-600 overflow-x-auto max-h-96 whitespace-pre-wrap">
                    {JSON.stringify(groupDetail, null, 2)}
                  </pre>
                  <p className="text-[10px] text-slate-400 mt-3 italic">
                    I dettagli completi del prodotto (varianti, quantita, prezzi) saranno visualizzati in un formato user-friendly
                    una volta che le credenziali saranno attive e il catalogo sara caricato.
                  </p>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Dettagli non disponibili</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Orders */}
      {tab === 'orders' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-xs text-slate-500">Storico ordini di stampa</p>
            <button onClick={loadOrders} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><RefreshCw size={16} /></button>
          </div>
          {orders.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
              <FileText size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">Nessun ordine di stampa</p>
              <p className="text-xs text-slate-400 mt-1">Sfoglia il catalogo e crea il tuo primo ordine</p>
            </div>
          ) : (
            orders.map(order => {
              const st = STATUS_MAP[order.status] || STATUS_MAP.placed;
              const date = order.created_at ? new Date(order.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
              return (
                <div key={order.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`fa-order-${order.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: st.color + '15' }}>
                        <Printer size={16} style={{ color: st.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[#1A1A1A]">{order.flyeralarm_order_id || 'Ordine'}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: st.color + '15', color: st.color }}>{st.label}</span>
                        </div>
                        <p className="text-[10px] text-slate-400">{date} · {order.delivery_address || ''}</p>
                      </div>
                    </div>
                    {order.print_data_uploaded ? (
                      <span className="text-[10px] text-green-600 font-semibold flex items-center gap-1"><CheckCircle size={10} /> File caricato</span>
                    ) : (
                      <span className="text-[10px] text-amber-600 font-semibold flex items-center gap-1"><Upload size={10} /> In attesa file</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
